import { useNavigate, Outlet } from "react-router-dom";
import {
  ApplicationBar,
  LayoutGrid,
  Sidebar,
  TalentechIcons,
  SupportBar,
  SplashScreen,
  TalentechSvgImages,
  Icon,
  TalentechSizes,
  Avatar,
  TalentechAvatarSizes,
} from "@talentech/components";
import {
  getUser,
  getUserSupportStatus,
  login,
  logout,
  UserType,
} from "@talentech/bff-login";
import { useDispatch, useSelector } from "react-redux";
import { useGetPagesQuery } from "./redux/reducer/pages.api";
import {
  useGetAppsQuery,
  useGetTenantsQuery,
} from "./redux/reducer/navigation.api";
import { IDropdownMenuItemProps } from "@talentech/components/build/storybook-core/components/DropdownMenu/DropdownMenuItem/DropdownMenuItem";
import "./App.css";

export const ROUTES = {
  HOME: "/",
  PAGES: "/pages",

  JOBS: "/jobs",

  DIRECT_JOBS: "/job-posts",
  DIRECT_JOB: "/job-posts/:jobId",

  EDITOR: "/editor/:type/:id",

  MEDIA_LIBRARY: "/media-library",
  FAVICONS: "/favicons",
  
  SUPPORT: "/support",

  SETTINGS: "/settings",
  SETTINGS_PRIVACY: "/settings/privacy",
};

export enum PageTypes {
  PAGE = "page",
  JOB = "job",
}

export enum PageStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
}

const App = () => {
  const { isLoading } = useGetPagesQuery({});
  const user = useSelector(getUser);
  const dispatch = useDispatch();
  const isSupport = useSelector(getUserSupportStatus);

  const { tenants } = useGetTenantsQuery(
    {},
    {
      selectFromResult: ({ data, ...rest }) => ({
        tenants: data?.tenants || [],
        ...rest,
      }),
    }
  );

  if (isLoading) {
    return (
      <SplashScreen
        image={TalentechSvgImages.WORK_SEARCHING_MAGNIFY}
        productName="Career Sites"
        title="Loading your data..."
        isFullPage
      />
    );
  }

  const onLogout = () => {
    dispatch(logout());
  };

  function openUrlInNewTab(url: string): void {
    window.open(url, "_blank");
  }

  return (
    <>
      {isSupport && (
        <SupportBar
          slots={[
            <>
              User: {user.given_name} {user.family_name}
            </>,
            <>Tenant: {user.tenantName}</>,
            <a
              className="d-flex text-light cursor-pointer text-decoration-none ms-4"
              onClick={() =>
                dispatch(
                  login({ userType: UserType.SUPPORT, forceLoginPrompt: true })
                )
              }
            >
              <Icon
                className="d-flex align-items-center pe-2"
                icon={
                  TalentechIcons.REGULAR_01_INTERFACE_ESSENTIALS_SYNCHRONIZE_ARROWS_SQUARE_1
                }
                iconSize={TalentechSizes.SMALL}
              />
              <span className="d-none d-lg-block">Switch customer</span>
            </a>,
          ]}
        />
      )}
      <LayoutGrid
        header={
          <ApplicationBar
            // @ts-ignore
            avatarUrl={user?.picture}
            // @ts-ignore
            firstName={user?.given_name}
            // @ts-ignore
            lastName={user?.family_name}
            activeWorkspace={user.tenantName}
            menu={[
              {
                title: "Switch workspace",
                dropdownItem: true,
                advancedProps: {
                  subtitle: tenants?.find((t) => t.isActive)?.name,
                  contentImage: (
                    <Avatar
                      outline
                      icon={
                        TalentechIcons.BOLD_17_USERS_GEOMETRIC_FULL_BODY_SINGLE_USER_NEUTRAL_SINGLE_NEUTRAL_ROTATE
                      }
                      size={TalentechAvatarSizes.LG}
                    />
                  ),
                },
                submenu: tenants.map((t) => ({
                  icon: TalentechIcons.BOLD_01_INTERFACE_ESSENTIALS_ALERTS_PLUS,
                  label: t.name,
                  active: t.isActive,
                  onClick: () => dispatch(login({ tenantId: t.id })),
                })) as IDropdownMenuItemProps[],
              },
              {
                title: "Manage Account",
                onClick: () =>
                  openUrlInNewTab(process.env.TID_API_URL + "/profile"),
                advancedProps: {
                  subtitle: user.email,
                  contentImage: (
                    <Avatar
                      size={TalentechAvatarSizes.LG}
                      outline
                      icon={
                        TalentechIcons.REGULAR_01_INTERFACE_ESSENTIALS_SETTING_COG
                      }
                    />
                  ),
                },
              },
              {
                title: "Logout",
                onClick: onLogout,
                advancedProps: {
                  contentImage: (
                    <Avatar
                      size={TalentechAvatarSizes.LG}
                      outline
                      icon={
                        TalentechIcons.REGULAR_01_INTERFACE_ESSENTIALS_LOGIN_LOGOUT_LOGOUT
                      }
                    />
                  ),
                },
              },
            ]}
            toolboxItems={[]}
          />
        }
        sidebar={<AppSidebar />}
      >
        <Outlet />
      </LayoutGrid>
    </>
  );
};

const AppSidebar = (props: { toggleSidebar?(): void }) => {
  const navigate = useNavigate();
  const nav = useGetAppsQuery({});
  return (
    <Sidebar
      activeApp={{
        appIconUrl:
          "https://storybook.talentech.io/images/svg/products/light/recruitment.svg",
        name: "Career Sites",
        id: "career-sites",
        disclaimer: "by Talentech",
      }}
      appSwitcher={nav.data}
      activeLanguage={"en"}
      menu={[
        {
          sectionName: "",
          menuItems: [
            {
              label: "Home",
              onClick: () =>
                navigate(ROUTES.HOME, {
                  viewTransition: true,
                }),
              icon: <i className="fa-sharp fa-light fa-home fs-5"></i>,
              id: "Home",
            },
            {
              label: "Jobs",
              onClick: () =>
                navigate(ROUTES.DIRECT_JOBS, {
                  viewTransition: true,
                }),
              icon: <i className="fa-sharp fa-light fa-briefcase fs-5"></i>,
              id: "jobs",
            },

            {
              label: "Job listing",
              onClick: () =>
                navigate(ROUTES.PAGES, {
                  viewTransition: true,
                }),
              icon: <i className="fa-sharp fa-light fa-signs-post fs-5"></i>,
              id: "job-listing",
            },
            {
              label: "Job ads",
              onClick: () =>
                navigate(ROUTES.JOBS, {
                  viewTransition: true,
                }),
              icon: <i className="fa-sharp fa-light fa-sidebar fs-5"></i>,
              id: "job-ads",
            },
          ],
        },
        {
          sectionName: "Media",
          menuItems: [
            {
              label: "Favicons",
              onClick: () =>
                navigate(ROUTES.FAVICONS, {
                  viewTransition: true,
                }),
              icon: <i className="fa-sharp fa-light fa-star fs-5"></i>,
              id: "favicons",
            },
            {
              label: "Media Library",
              onClick: () =>
                navigate(ROUTES.MEDIA_LIBRARY, {
                  viewTransition: true,
                }),
              icon: <i className="fa-sharp fa-light fa-images fs-5"></i>,
              id: "media-library",
            },
          ],
        },
        {
          sectionName: "Settings",
          menuItems: [
            {
              label: "Privacy",
              onClick: () =>
                navigate(ROUTES.SETTINGS_PRIVACY, {
                  viewTransition: true,
                }),
              icon: <i className="fa-sharp fa-light fa-shield-halved fs-5"></i>,
              id: "privacy",
            },
          ],
        }
      ]}
      {...props}
    />
  );
};

export default App;
